// communityDetail/view.js
import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  CircularProgress,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Settings as SettingsIcon,
  People as PeopleIcon,
  Event as EventIcon,
  Store as StoreIcon,
  Assessment as AssessmentIcon,
  Quiz as QuizIcon,
  Refresh as RefreshIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  Archive as ArchiveIcon,
  Share as ShareIcon,
  LocalOffer as TagIcon,
} from '@mui/icons-material';

import { API_BASE_URL } from 'config/api';

import {
  PageContainer,
  HeaderSection,
  HeaderContent,
  CommunityAvatar,
  HeaderInfo,
  CommunityName,
  CommunitySlug,
  StatusBadge,
  VisibilityBadge,
  QuickStats,
  QuickStat,
  StatNumber,
  StatLabel,
  StyledTabs,
  StyledTab,
  TabPanel,
  LoadingContainer,
  ActionButton,
  honeyColors,
} from './styled';

// Import tab components
import OverviewTab from './tabs/OverviewTab';
import SettingsTab from './tabs/SettingsTab';
import MembersTab from './tabs/MembersTab';
import VenuesTab from './tabs/VenuesTab';
import EventsTab from './tabs/EventsTab';
import QuestionsTab from './tabs/QuestionsTab';

const getStatusText = (status) => {
  const map = {
    'AC': 'Active',
    'PP': 'Pending',
    'IN': 'Inactive',
    'RE': 'Rejected',
    'BA': 'Banned',
  };
  return map[status] || 'Unknown';
};

const getVisibilityText = (visibility) => {
  const map = {
    'PU': 'Public',
    'PR': 'Private',
    'SE': 'Secret',
  };
  return map[visibility] || 'Unknown';
};

const CommunityDetailView = ({
  // Data
  community,
  members,
  allMembers,
  venues,
  upcomingEvents,
  admissionQuestions,
  availableTags,
  tariffs,
  formData,
  stats,
  userRole,
  
  // State
  loading,
  saving,
  activeTab,
  hasChanges,
  snackbar,
  memberFilters,
  
  // Handlers
  onTabChange,
  onFormChange,
  onSave,
  onCancel,
  onBack,
  onImageUpload,
  onMemberAction,
  onMemberFilterChange,
  onInviteMember,
  onCloseSnackbar,
  onTagAdd,
  onTagRemove,
  onQuestionAdd,
  onQuestionUpdate,
  onQuestionDelete,
  onQuestionReorder,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Helper to get image URL
  const getImageUrl = (image) => {
    if (!image) return null;
    if (typeof image === 'object') return URL.createObjectURL(image);
    return image.startsWith('/') ? `${API_BASE_URL}${image}` : image;
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingContainer>
          <CircularProgress size={60} sx={{ color: honeyColors.primary }} />
        </LoadingContainer>
      </PageContainer>
    );
  }

  if (!community) {
    return (
      <PageContainer>
        <Box textAlign="center" py={8}>
          <Typography variant="h5" color="textSecondary">
            Community not found
          </Typography>
          <ActionButton variant="contained" onClick={onBack} sx={{ mt: 2 }}>
            Go Back
          </ActionButton>
        </Box>
      </PageContainer>
    );
  }

  // Check if user has full admin access (FO, AD, MA) or limited access (EO - Event Organizer)
  const isFullAdmin = ['FO', 'AD', 'MA'].includes(userRole);

  // Define all tabs with access control
  const allTabs = [
    { label: 'Overview', icon: <AssessmentIcon />, key: 'overview', access: 'all' },
    { label: 'Settings', icon: <SettingsIcon />, key: 'settings', access: 'admin' },
    { label: 'Members', icon: <PeopleIcon />, badge: stats.pendingMembers > 0 ? stats.pendingMembers : null, key: 'members', access: 'admin' },
    { label: 'Venues', icon: <StoreIcon />, key: 'venues', access: 'admin' },
    { label: 'Events', icon: <EventIcon />, key: 'events', access: 'all' },
    { label: 'Questions', icon: <QuizIcon />, key: 'questions', access: 'admin' },
  ];

  // Filter tabs based on user role - Event Organizers see only Overview and Events
  const tabs = isFullAdmin 
    ? allTabs 
    : allTabs.filter(tab => tab.access === 'all');

  return (
    <PageContainer>
      {/* Header Section */}
      <HeaderSection>
        <HeaderContent>
          {/* Back button */}
          <IconButton 
            onClick={onBack}
            sx={{ 
              position: 'absolute', 
              top: 16, 
              left: 16,
              backgroundColor: honeyColors.surfaceHoney,
              '&:hover': { backgroundColor: honeyColors.border },
            }}
          >
            <ArrowBackIcon />
          </IconButton>

          {/* Community Avatar */}
          <CommunityAvatar
            src={getImageUrl(community.image)}
            alt={community.name}
          >
            {community.name?.charAt(0)?.toUpperCase()}
          </CommunityAvatar>

          {/* Community Info */}
          <HeaderInfo>
            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
              <CommunityName>{community.name}</CommunityName>
              <StatusBadge 
                status={community.status} 
                label={getStatusText(community.status)} 
                size="small"
              />
              <VisibilityBadge 
                visibility={community.visibility} 
                label={getVisibilityText(community.visibility)} 
                size="small"
              />
            </Box>
            
            <CommunitySlug>/{community.slug}</CommunitySlug>
            
            {community.description && (
              <Typography 
                color="textSecondary" 
                sx={{ 
                  maxWidth: '600px',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {community.description}
              </Typography>
            )}

            {/* Quick Stats */}
            <QuickStats>
              <QuickStat>
                <StatNumber>{stats.totalMembers}</StatNumber>
                <StatLabel>Members</StatLabel>
              </QuickStat>
              <QuickStat>
                <StatNumber>{stats.activeMembers}</StatNumber>
                <StatLabel>Active</StatLabel>
              </QuickStat>
              <QuickStat>
                <StatNumber>{stats.pendingMembers}</StatNumber>
                <StatLabel>Pending</StatLabel>
              </QuickStat>
              <QuickStat>
                <StatNumber>{stats.totalVenues}</StatNumber>
                <StatLabel>Venues</StatLabel>
              </QuickStat>
              <QuickStat>
                <StatNumber>{stats.upcomingEvents}</StatNumber>
                <StatLabel>Events</StatLabel>
              </QuickStat>
            </QuickStats>
          </HeaderInfo>

          {/* Actions Menu */}
          <Box sx={{ position: 'absolute', top: 16, right: 16 }}>
            <IconButton onClick={handleMenuOpen}>
              <MoreVertIcon />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={menuOpen}
              onClose={handleMenuClose}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <MenuItem onClick={handleMenuClose}>
                <ListItemIcon><ShareIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Share Community</ListItemText>
              </MenuItem>
              <MenuItem onClick={handleMenuClose}>
                <ListItemIcon><ArchiveIcon fontSize="small" /></ListItemIcon>
                <ListItemText>Archive Community</ListItemText>
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleMenuClose} sx={{ color: honeyColors.error }}>
                <ListItemIcon><DeleteIcon fontSize="small" sx={{ color: honeyColors.error }} /></ListItemIcon>
                <ListItemText>Delete Community</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </HeaderContent>
      </HeaderSection>

      {/* Tabs */}
      <StyledTabs
        value={activeTab}
        onChange={onTabChange}
        variant="scrollable"
        scrollButtons="auto"
      >
        {tabs.map((tab, index) => (
          <StyledTab
            key={index}
            label={
              <Box display="flex" alignItems="center" gap={1}>
                {tab.icon}
                <span>{tab.label}</span>
                {tab.badge && (
                  <Box
                    sx={{
                      backgroundColor: honeyColors.warning,
                      color: '#fff',
                      borderRadius: '10px',
                      padding: '2px 8px',
                      fontSize: '0.7rem',
                      fontWeight: 700,
                    }}
                  >
                    {tab.badge}
                  </Box>
                )}
              </Box>
            }
          />
        ))}
      </StyledTabs>

      {/* Tab Panels - render based on selected tab key */}
      <TabPanel>
        {tabs[activeTab]?.key === 'overview' && (
          <OverviewTab
            community={community}
            stats={stats}
            members={allMembers}
            venues={venues}
            upcomingEvents={upcomingEvents}
          />
        )}
        
        {tabs[activeTab]?.key === 'settings' && (
          <SettingsTab
            formData={formData}
            availableTags={availableTags}
            saving={saving}
            hasChanges={hasChanges}
            onFormChange={onFormChange}
            onSave={onSave}
            onCancel={onCancel}
            onImageUpload={onImageUpload}
            onTagAdd={onTagAdd}
            onTagRemove={onTagRemove}
          />
        )}
        
        {tabs[activeTab]?.key === 'members' && (
          <MembersTab
            members={members}
            allMembers={allMembers}
            filters={memberFilters}
            saving={saving}
            onFilterChange={onMemberFilterChange}
            onMemberAction={onMemberAction}
            onInviteMember={onInviteMember}
          />
        )}
        
        {tabs[activeTab]?.key === 'venues' && (
          <VenuesTab
            venues={venues}
            community={community}
          />
        )}
        
        {tabs[activeTab]?.key === 'events' && (
          <EventsTab
            events={upcomingEvents}
            community={community}
          />
        )}
        
        {tabs[activeTab]?.key === 'questions' && (
          <QuestionsTab
            questions={admissionQuestions}
            requiresQuestions={formData.requires_admission_questions}
            saving={saving}
            onFormChange={onFormChange}
            onQuestionAdd={onQuestionAdd}
            onQuestionUpdate={onQuestionUpdate}
            onQuestionDelete={onQuestionDelete}
            onQuestionReorder={onQuestionReorder}
          />
        )}
      </TabPanel>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={onCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={onCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};

export default CommunityDetailView;
